import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  FlagIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  calculateMultiPortfolioGoalValue,
  calculateProgressPercentage,
  calculateGoalStatus,
  calculateGoalProjection,
  calculateGoalMonthlyContribution,
  convertCurrency,
} from '../../utils/calculations';
import {
  formatCurrency,
} from '../../utils/formatters';
import { FINANCIAL_COLORS } from '../../utils/colors';
import { getGoalIcon } from '../../utils/goalIcons';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import ProposedContributions from './ProposedContributions';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const SortableGoalCard = ({ goal, isSelected, onHover, onLeave, onClick, goalSavings, settings }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: goal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  const GoalIcon = getGoalIcon(goal.icon || goal.emoji);
  const reachDate = goal.projection.goalReachDateAverage;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onClick(goal.id, isSelected)}
      onMouseEnter={() => onHover(goal.id)}
      onMouseLeave={onLeave}
      className={`rounded-lg border p-2.5 cursor-pointer transition-all ${
        isDragging ? 'shadow-lg border-gray-400 bg-white' :
        isSelected ? 'border-gray-400 shadow-sm bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-2.5">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="8" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="2" cy="14" r="1.5" />
            <circle cx="8" cy="14" r="1.5" />
          </svg>
        </div>

        <div className="flex-shrink-0 relative w-9 h-9">
          <svg width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#f3f4f6" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="14"
              fill="none" stroke={goal.color} strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 14}
              strokeDashoffset={2 * Math.PI * 14 - (Math.min(goal.progress, 100) / 100) * 2 * Math.PI * 14}
              transform="rotate(-90 18 18)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {goal.progress >= 100 ? (
              <CheckCircleIcon className="w-4 h-4 text-green-600" />
            ) : (
              <span className="text-[10px] font-bold text-gray-700">{Math.round(goal.progress)}%</span>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <GoalIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate">{goal.name}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-700 font-semibold">
              {formatCurrency(goal.currentValueInDisplay, settings.displayCurrency, 0)}
            </span>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs text-gray-500">
              {formatCurrency(goal.targetInDisplay, settings.displayCurrency, 0)}
            </span>
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          {goal.status.status === 'achieved' ? (
            <span className="text-xs font-medium text-green-600">Achieved</span>
          ) : reachDate ? (
            <div>
              <p className="text-xs text-gray-500">Est. reach</p>
              <p className="text-xs font-semibold text-gray-900">{reachDate.format('MMM YYYY')}</p>
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>
      </div>
      {/* Inline savings breakdown */}
      {goalSavings && (
        <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-gray-100 text-[10px]">
          <span className="text-gray-400">Saved:</span>
          <span><span className="text-gray-400">Mo </span><span className="font-bold text-gray-700">{goalSavings.thisMonth > 0 ? formatCurrency(goalSavings.thisMonth, settings.displayCurrency, 0) : '\u2013'}</span></span>
          <span><span className="text-gray-400">3mo </span><span className="font-bold text-gray-700">{goalSavings.threeMonths > 0 ? formatCurrency(goalSavings.threeMonths, settings.displayCurrency, 0) : '\u2013'}</span></span>
          <span><span className="text-gray-400">YTD </span><span className="font-bold text-gray-700">{goalSavings.thisYear > 0 ? formatCurrency(goalSavings.thisYear, settings.displayCurrency, 0) : '\u2013'}</span></span>
          <span><span className="text-gray-400">All </span><span className="font-bold text-gray-700">{goalSavings.allTime > 0 ? formatCurrency(goalSavings.allTime, settings.displayCurrency, 0) : '\u2013'}</span></span>
        </div>
      )}
    </div>
  );
};

const DashboardV3 = () => {
  const navigate = useNavigate();
  const { portfolios, goals, transactions, proposedTransactions, settings, exchangeRates, properties, mortgages, reorderGoals } = useData();
  const [selectedGoalId, setSelectedGoalId] = useState(null);

  // Net worth calculations
  const totalInvestmentValue = portfolios.reduce((sum, p) => {
    return sum + convertCurrency(p.currentValue, p.currency, settings.displayCurrency, exchangeRates);
  }, 0);

  const totalPropertyValue = (properties || []).reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalMortgageBalance = (mortgages || []).reduce((sum, m) => sum + (m.currentBalance || 0), 0);
  const propertyEquity = totalPropertyValue - totalMortgageBalance;
  const hasRealEstate = totalPropertyValue > 0 || totalMortgageBalance > 0;
  const totalNetWorth = totalInvestmentValue + propertyEquity;

  // Goal data with projections
  const goalData = useMemo(() => {
    return goals.map((goal, index) => {
      const currentValue = calculateMultiPortfolioGoalValue(goal, portfolios, exchangeRates);
      const goalCurrency = goal.currency || 'GBP';
      const currentValueInDisplay = convertCurrency(currentValue, goalCurrency, settings.displayCurrency, exchangeRates);
      const targetInDisplay = convertCurrency(goal.targetAmount, goalCurrency, settings.displayCurrency, exchangeRates);
      const progress = calculateProgressPercentage(currentValue, goal.targetAmount);
      const status = calculateGoalStatus(goal, currentValue, portfolios, exchangeRates);
      const monthlyContribution = calculateGoalMonthlyContribution(goal, portfolios, exchangeRates);
      const projection = calculateGoalProjection(goal, portfolios, transactions, exchangeRates);
      const color = goal.color || FINANCIAL_COLORS[index % FINANCIAL_COLORS.length];

      return {
        ...goal,
        currentValue,
        currentValueInDisplay,
        targetInDisplay,
        progress,
        status,
        monthlyContribution,
        projection,
        color,
        goalCurrency,
      };
    });
  }, [goals, portfolios, transactions, settings.displayCurrency, exchangeRates]);

  // Build combined projection chart data showing all goals
  const chartData = useMemo(() => {
    if (goalData.length === 0) return [];

    let maxMonths = 0;
    goalData.forEach(goal => {
      if (goal.projection.averageProjection) {
        maxMonths = Math.max(maxMonths, goal.projection.averageProjection.length);
      }
    });

    if (maxMonths === 0) return [];

    const step = maxMonths <= 60 ? 3 : 6;
    const data = [];

    for (let i = 0; i < maxMonths; i += step) {
      const point = { month: i };
      let hasAnyData = false;

      goalData.forEach((goal, gIndex) => {
        const avgProj = goal.projection.averageProjection;
        if (avgProj && avgProj[i]) {
          point[`goal_${gIndex}`] = avgProj[i].value;
          point[`goal_${gIndex}_name`] = goal.name;
          point.displayDate = avgProj[i].displayDate;
          hasAnyData = true;
        }
      });

      if (hasAnyData) data.push(point);
    }

    const lastIdx = maxMonths - 1;
    if (lastIdx % step !== 0) {
      const lastPoint = { month: lastIdx };
      goalData.forEach((goal, gIndex) => {
        const avgProj = goal.projection.averageProjection;
        if (avgProj && avgProj[lastIdx]) {
          lastPoint[`goal_${gIndex}`] = avgProj[lastIdx].value;
          lastPoint[`goal_${gIndex}_name`] = goal.name;
          lastPoint.displayDate = avgProj[lastIdx].displayDate;
        }
      });
      data.push(lastPoint);
    }

    return data;
  }, [goalData]);

  // Account type totals
  const accountTypeTotals = useMemo(() => {
    const grouped = {};
    portfolios.forEach(portfolio => {
      const type = portfolio.type || 'Other';
      if (!grouped[type]) grouped[type] = 0;
      grouped[type] += convertCurrency(portfolio.currentValue, portfolio.currency, settings.displayCurrency, exchangeRates);
    });
    return Object.entries(grouped).sort((a, b) => b[1] - a[1]);
  }, [portfolios, settings.displayCurrency, exchangeRates]);

  // Contribution totals — only counts transactions confirmed through the proposed contribution system
  const contributionTotals = useMemo(() => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const sixMonthsAgo = now.subtract(6, 'month').startOf('month');
    const startOfYear = now.startOf('year');

    const confirmedTxnIds = new Set(
      proposedTransactions.filter(p => p.status === 'confirmed' && p.transactionId).map(p => p.transactionId)
    );
    const contributions = transactions.filter(t => confirmedTxnIds.has(t.id));

    const sumContributions = (filtered) =>
      filtered.reduce((sum, t) => {
        const currency = portfolios.find(p => p.id === t.portfolioId)?.currency || settings.displayCurrency;
        return sum + convertCurrency(t.amount, currency, settings.displayCurrency, exchangeRates);
      }, 0);

    return {
      thisMonth: sumContributions(contributions.filter(t => !dayjs(t.date).isBefore(startOfMonth))),
      lastSixMonths: sumContributions(contributions.filter(t => !dayjs(t.date).isBefore(sixMonthsAgo))),
      thisYear: sumContributions(contributions.filter(t => !dayjs(t.date).isBefore(startOfYear))),
      allTime: sumContributions(contributions),
    };
  }, [transactions, proposedTransactions, portfolios, settings.displayCurrency, exchangeRates]);

  // Monthly contribution plan — which accounts contribute and where it goes
  const contributionPlan = useMemo(() => {
    const plan = [];
    portfolios.forEach(portfolio => {
      if (!portfolio.monthlyContribution || portfolio.monthlyContribution <= 0) return;

      const amountInDisplay = convertCurrency(portfolio.monthlyContribution, portfolio.currency, settings.displayCurrency, exchangeRates);

      // Find which goals this account feeds via allocations
      const goalBreakdown = [];
      goals.forEach(goal => {
        if (!goal.allocations) return;
        goal.allocations.forEach(alloc => {
          if (alloc.portfolioId !== portfolio.id) return;
          const percentValue = alloc.type === 'percentage' || !alloc.type
            ? (alloc.value !== undefined ? alloc.value : alloc.percentage) || 0
            : 0;
          const fixedValue = (alloc.type === 'value' || alloc.type === 'amount') ? (alloc.value || 0) : 0;

          let contributionToGoal = 0;
          if (percentValue > 0) {
            contributionToGoal = (portfolio.monthlyContribution * percentValue) / 100;
          } else if (fixedValue > 0) {
            // For fixed allocations, proportion based on fixed/total ratio
            const portfolioValue = portfolio.currentValue || 1;
            const fixedRatio = Math.min(1, fixedValue / portfolioValue);
            contributionToGoal = portfolio.monthlyContribution * fixedRatio;
          }

          if (contributionToGoal > 0) {
            goalBreakdown.push({
              goalId: goal.id,
              goalName: goal.name,
              goalEmoji: goal.emoji,
              goalIcon: goal.icon,
              amount: convertCurrency(contributionToGoal, portfolio.currency, settings.displayCurrency, exchangeRates),
              percentage: percentValue,
              type: alloc.type || 'percentage',
            });
          }
        });
      });

      plan.push({
        id: portfolio.id,
        name: portfolio.name,
        type: portfolio.type,
        currency: portfolio.currency,
        monthlyContribution: portfolio.monthlyContribution,
        amountInDisplay,
        contributionDay: portfolio.contributionDay,
        goalBreakdown,
      });
    });
    return plan;
  }, [portfolios, goals, settings.displayCurrency, exchangeRates]);

  const totalMonthlyContributions = contributionPlan.reduce((sum, p) => sum + p.amountInDisplay, 0);

  // Unallocated funds — accounts (or portions) not assigned to any goal
  const unallocatedAccounts = useMemo(() => {
    return portfolios.map(portfolio => {
      const valueInDisplay = convertCurrency(portfolio.currentValue, portfolio.currency, settings.displayCurrency, exchangeRates);

      // Sum up how much of this portfolio is allocated across all goals
      let totalAllocatedPercent = 0;
      let totalAllocatedFixed = 0;

      goals.forEach(goal => {
        if (!goal.allocations) return;
        goal.allocations.forEach(alloc => {
          if (alloc.portfolioId !== portfolio.id) return;
          if (alloc.type === 'value' || alloc.type === 'amount') {
            totalAllocatedFixed += convertCurrency(alloc.value || 0, goal.currency || 'GBP', settings.displayCurrency, exchangeRates);
          } else {
            totalAllocatedPercent += (alloc.value !== undefined ? alloc.value : alloc.percentage) || 0;
          }
        });
      });

      const allocatedFromPercent = valueInDisplay * Math.min(totalAllocatedPercent, 100) / 100;
      const totalAllocated = allocatedFromPercent + totalAllocatedFixed;
      const unallocated = Math.max(0, valueInDisplay - totalAllocated);

      return {
        id: portfolio.id,
        name: portfolio.name,
        type: portfolio.type,
        totalValue: valueInDisplay,
        allocated: totalAllocated,
        unallocated,
        allocatedPercent: valueInDisplay > 0 ? Math.min(100, (totalAllocated / valueInDisplay) * 100) : 0,
      };
    }).filter(a => a.unallocated > 0.01);
  }, [portfolios, goals, settings.displayCurrency, exchangeRates]);

  const totalUnallocated = unallocatedAccounts.reduce((sum, a) => sum + a.unallocated, 0);

  // Per-goal savings breakdown across time periods (all contribution transactions, not just confirmed proposals)
  const goalSavingsBreakdown = useMemo(() => {
    if (goals.length === 0) return [];

    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const threeMonthsAgo = now.subtract(3, 'month').startOf('month');
    const startOfYear = now.startOf('year');

    // All inbound money transactions — contribution (confirmed proposals), manual (user-entered
    // deposits / initial balances), and opening balances. Exclude value-adjustments (market moves)
    // and withdrawals. Only positive amounts represent money going in.
    const contributions = transactions.filter(t =>
      (t.type === 'contribution' || t.type === 'manual' || t.type === 'opening') &&
      t.amount > 0
    );

    return goals.map(goal => {
      if (!goal.allocations || goal.allocations.length === 0) {
        return { goalId: goal.id, goalName: goal.name, goalColor: goal.color, thisMonth: 0, threeMonths: 0, thisYear: 0, allTime: 0 };
      }

      // Build a map of portfolioId -> allocation percentage for this goal
      const allocMap = {};
      goal.allocations.forEach(alloc => {
        const pct = (alloc.type === 'percentage' || !alloc.type)
          ? (alloc.value !== undefined ? alloc.value : alloc.percentage) || 0
          : 0;
        // For fixed-value allocations, estimate percentage from current value
        let effectivePct = pct;
        if ((alloc.type === 'value' || alloc.type === 'amount') && alloc.value) {
          const portfolio = portfolios.find(p => p.id === alloc.portfolioId);
          if (portfolio && portfolio.currentValue > 0) {
            effectivePct = Math.min(100, (alloc.value / portfolio.currentValue) * 100);
          }
        }
        if (effectivePct > 0) {
          allocMap[alloc.portfolioId] = (allocMap[alloc.portfolioId] || 0) + effectivePct;
        }
      });

      const sumForPeriod = (filtered) =>
        filtered.reduce((sum, t) => {
          const pct = allocMap[t.portfolioId];
          if (!pct) return sum;
          const currency = portfolios.find(p => p.id === t.portfolioId)?.currency || settings.displayCurrency;
          const amount = convertCurrency(t.amount * (pct / 100), currency, settings.displayCurrency, exchangeRates);
          return sum + amount;
        }, 0);

      // "All time" uses current allocated portfolio value (via calculateMultiPortfolioGoalValue)
      // rather than transaction sums, so accounts that track value via holdings (not transactions)
      // are correctly included.
      const allTimeValue = convertCurrency(
        calculateMultiPortfolioGoalValue(goal, portfolios, exchangeRates),
        goal.currency || 'GBP',
        settings.displayCurrency,
        exchangeRates
      );

      return {
        goalId: goal.id,
        goalName: goal.name,
        goalColor: goal.color,
        thisMonth: sumForPeriod(contributions.filter(t => !dayjs(t.date).isBefore(startOfMonth))),
        threeMonths: sumForPeriod(contributions.filter(t => !dayjs(t.date).isBefore(threeMonthsAgo))),
        thisYear: sumForPeriod(contributions.filter(t => !dayjs(t.date).isBefore(startOfYear))),
        allTime: allTimeValue,
      };
    });
  }, [goals, transactions, portfolios, settings.displayCurrency, exchangeRates]);

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = goalData.findIndex(g => g.id === active.id);
    const newIndex = goalData.findIndex(g => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = goalData.map(g => g.id);
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, active.id);
    reorderGoals(newOrder);
  };

  return (
    <div className="max-w-7xl mx-12">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Net Worth</p>
          <span className="text-3xl font-bold text-gray-900">
            {formatCurrency(hasRealEstate ? totalNetWorth : totalInvestmentValue, settings.displayCurrency, 0)}
          </span>
        </div>
        <div className="flex gap-3 text-xs text-gray-500">
          {accountTypeTotals.map(([type, value], index) => (
            <span key={type} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: FINANCIAL_COLORS[index % FINANCIAL_COLORS.length] }} />
              {type}: {formatCurrency(value, settings.displayCurrency, 0)}
            </span>
          ))}
          {hasRealEstate && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block bg-blue-400" />
              Property: {formatCurrency(propertyEquity, settings.displayCurrency, 0)}
            </span>
          )}
        </div>
      </div>

      {goalData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Left column: Goals + Pending Contributions */}
          <div className="lg:col-span-1 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Funds</h3>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={goalData.map(g => g.id)} strategy={verticalListSortingStrategy}>
                <div className="max-h-[calc(100vh-12rem)] overflow-y-auto space-y-2 pr-1">
                {goalData.map((goal) => (
                  <SortableGoalCard
                    key={goal.id}
                    goal={goal}
                    isSelected={selectedGoalId === goal.id}
                    onHover={(id) => setSelectedGoalId(id)}
                    onLeave={() => setSelectedGoalId(null)}
                    onClick={(id, isSelected) => {
                      setSelectedGoalId(isSelected ? null : id);
                      navigate(`/funds/${id}`);
                    }}
                    goalSavings={goalSavingsBreakdown.find(g => g.goalId === goal.id)}
                    settings={settings}
                  />
                ))}
                </div>
              </SortableContext>
            </DndContext>

          </div>

          {/* Right column: Chart + Contribution Summary */}
          <div className="lg:col-span-2 space-y-3">
            {/* Projection Chart */}
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Projections</h3>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-xs font-semibold text-gray-900">Fund Projections</h3>
                  <p className="text-[10px] text-gray-500">Average return scenario</p>
                </div>
                {/* Legend inline with header */}
                <div className="flex flex-wrap gap-3">
                  {goalData.map((goal) => {
                    const GoalIcon = getGoalIcon(goal.icon || goal.emoji);
                    return (
                      <div
                        key={goal.id}
                        className="flex items-center gap-1 text-xs cursor-pointer px-1.5 py-0.5 rounded hover:bg-gray-50"
                        onMouseEnter={() => setSelectedGoalId(goal.id)}
                        onMouseLeave={() => setSelectedGoalId(null)}
                      >
                        <span className="w-3 h-0.5 rounded" style={{ backgroundColor: goal.color }} />
                        <span className="text-gray-600">{goal.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={formatYAxis}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload) return null;
                        return (
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
                            <p className="font-semibold mb-1">{label}</p>
                            {payload.filter(p => p.value != null).map((entry, i) => {
                              const goalIdx = parseInt(entry.dataKey.split('_')[1]);
                              const goal = goalData[goalIdx];
                              return (
                                <div key={i} className="flex items-center gap-2 mt-1">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                  <span className="text-gray-600">{goal?.name}:</span>
                                  <span className="font-semibold">{formatCurrency(entry.value, settings.displayCurrency, 0)}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    {goalData.map((goal, gIndex) => (
                      <React.Fragment key={goal.id}>
                        <Area
                          type="monotone"
                          dataKey={`goal_${gIndex}`}
                          fill={goal.color}
                          fillOpacity={selectedGoalId === goal.id ? 0.15 : 0.05}
                          stroke="none"
                        />
                        <Line
                          type="monotone"
                          dataKey={`goal_${gIndex}`}
                          stroke={goal.color}
                          strokeWidth={selectedGoalId === goal.id ? 3 : 2}
                          strokeOpacity={selectedGoalId && selectedGoalId !== goal.id ? 0.3 : 1}
                          dot={false}
                          name={goal.name}
                        />
                        <ReferenceLine
                          y={goal.targetInDisplay}
                          stroke={goal.color}
                          strokeDasharray="4 4"
                          strokeWidth={1}
                          strokeOpacity={0.5}
                        />
                      </React.Fragment>
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                  <FlagIcon className="w-10 h-10 mb-2" />
                  <p className="text-sm">Add funds with portfolio allocations to see projections</p>
                </div>
              )}
            </div>

            {/* Unallocated + Pending | Monthly Contributions side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Left: Unallocated + Pending Contributions */}
              <div className="space-y-2">
                {/* Unallocated Funds */}
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Unallocated Funds</h3>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-900">By Account</h3>
                    {totalUnallocated > 0 && (
                      <span className="text-xs font-bold text-amber-600">
                        {formatCurrency(totalUnallocated, settings.displayCurrency, 0)}
                      </span>
                    )}
                  </div>
                  {unallocatedAccounts.length > 0 ? (
                    <div className="space-y-1">
                      {unallocatedAccounts.map(account => (
                        <div key={account.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[11px] font-medium text-gray-900 truncate">{account.name}</span>
                            <span className="text-[9px] text-gray-400">{account.type}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full bg-blue-500"
                                style={{ width: `${account.allocatedPercent}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-semibold text-gray-700">
                              {formatCurrency(account.unallocated, settings.displayCurrency, 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-green-600">All funds allocated</p>
                  )}
                </div>

                {/* Pending Contributions */}
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Pending Contributions</h3>
                <ProposedContributions />
              </div>

              {/* Right: Monthly Contribution Plan */}
              <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Contributions</h3>
              {contributionPlan.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 h-full">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <BanknotesIcon className="w-4 h-4 text-green-600" />
                      <h3 className="text-xs font-semibold text-gray-900">Monthly Contributions</h3>
                    </div>
                    <span className="text-xs font-bold text-gray-900">
                      {formatCurrency(totalMonthlyContributions, settings.displayCurrency, 0)}/mo
                    </span>
                  </div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                    {contributionPlan.map(account => (
                      <div key={account.id} className="flex items-start justify-between gap-2 border border-gray-100 rounded p-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-gray-900 truncate">{account.name}</span>
                            <span className="text-[9px] text-gray-400">{account.type}</span>
                          </div>
                          {account.goalBreakdown.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {account.goalBreakdown.map((gb, i) => (
                                <span key={i} className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">
                                  {gb.goalName} <span className="text-blue-500 font-medium">{gb.type === 'percentage' ? `${gb.percentage}%` : formatCurrency(gb.amount, settings.displayCurrency, 0)}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[10px] text-gray-400 mt-0.5">Unallocated</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs font-bold text-gray-900">
                            {formatCurrency(account.amountInDisplay, settings.displayCurrency, 0)}
                          </span>
                          {account.contributionDay && (
                            <p className="text-[9px] text-gray-400">day {account.contributionDay}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Confirmed contribution totals */}
                  <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-[10px]">
                    <span className="text-gray-400">Confirmed:</span>
                    <div className="flex gap-3">
                      <span><span className="text-gray-400">Mo </span><span className="font-bold text-gray-700">{formatCurrency(contributionTotals.thisMonth, settings.displayCurrency, 0)}</span></span>
                      <span><span className="text-gray-400">6mo </span><span className="font-bold text-gray-700">{formatCurrency(contributionTotals.lastSixMonths, settings.displayCurrency, 0)}</span></span>
                      <span><span className="text-gray-400">YTD </span><span className="font-bold text-gray-700">{formatCurrency(contributionTotals.thisYear, settings.displayCurrency, 0)}</span></span>
                      <span><span className="text-gray-400">All </span><span className="font-bold text-gray-700">{formatCurrency(contributionTotals.allTime, settings.displayCurrency, 0)}</span></span>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>

          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <ProposedContributions />
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <FlagIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">No funds set yet</p>
            <p className="text-xs text-gray-400 mb-4">Create funds to track where your money is going</p>
            <button
              onClick={() => navigate('/funds')}
              className="px-4 py-2 text-white rounded-lg font-medium text-sm"
              style={{ backgroundColor: '#012E58' }}
            >
              Create Your First Fund
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardV3;
